<template>
  <div class="top-nav-bar" :class="[navBarClass, { hidden: isHidden }]">
    <div 
      class="nav-item" 
      :class="{ active: activeNav === 'home' }"
      @click="navigateTo('home')">
      <i class="el-icon-house"></i>
      <span>主页</span>
    </div>
    <div 
      class="nav-item" 
      :class="{ active: activeNav === 'more' }"
      @click="navigateTo('more')">
      <i class="el-icon-menu"></i>
      <span>更多</span>
    </div>
  </div>
</template>

<script>
import EventBus from '@/utils/EventBus'

export default {
  name: 'TopNavBarMobile',
  props: {
    classId: {
      type: [Number, String],
      required: true
    },
    subjectIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      // 控制导航栏是否隐藏
      isHidden: false,
      // 记录上一次滚动的Y坐标，用于判断滚动方向
      lastScrollY: 0,
      // 滚动阈值(px)，当滚动距离超过此值时才触发导航栏隐藏/显示
      scrollThreshold: 10
    }
  },
  computed: {
    activeNav() {
      const path = this.$route.path;
      if (path.includes('/mobile/more') || path.includes('/mobile/management') || path.includes('/mobile/rollcall') || path.includes('/mobile/penalty')) {
        return 'more';
      }
      return 'home';
    },
    currentSubjectIndex() {
      const subjectId = this.$route.query.subjectId;
      if (subjectId) {
        return parseInt(subjectId) - 1;
      }
      return this.subjectIndex;
    },
    navBarClass() {
      if (this.activeNav === 'more') {
        return 'management-theme';
      }
      switch(this.currentSubjectIndex) {
        case 0:
          return 'chinese-subject';
        case 1:
          return 'math-subject';
        case 2:
          return 'english-subject';
        default:
          return 'math-subject';
      }
    }
  },
  mounted() {
    // 组件挂载时添加滚动监听，使用passive模式提高滚动性能
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  },
  beforeDestroy() {
    // 组件销毁前移除滚动监听，避免内存泄漏
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    /**
     * 滚动事件处理函数
     * 实现逻辑：
     * - 向下滚动且滚动距离超过阈值时，隐藏导航栏
     * - 滚动到顶部附近（小于等于阈值）时，显示导航栏
     * - 向上滚动但未到顶部时，保持当前状态不变
     */
    handleScroll() {
      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - this.lastScrollY;
      
      // 向下滚动且超过阈值 -> 隐藏导航栏
      if (scrollDiff > 0 && currentScrollY > this.scrollThreshold) {
        this.isHidden = true;
        EventBus.$emit('navbar-visibility-changed', { visible: false });
      } 
      // 滚动到顶部附近 -> 显示导航栏
      else if (currentScrollY <= this.scrollThreshold) {
        this.isHidden = false;
        EventBus.$emit('navbar-visibility-changed', { visible: true });
      }
      
      // 更新上一次滚动位置
      this.lastScrollY = currentScrollY;
    },
    // 导航到指定页面
    navigateTo(nav) {
      if (nav === 'home') {
        if (this.activeNav !== 'home') {
          // 从sessionStorage恢复之前保存的科目状态
          const savedState = sessionStorage.getItem('mobileNavState');
          let query = {};
          if (savedState) {
            try {
              const state = JSON.parse(savedState);
              if (state.subjectIndex !== undefined) {
                query.subjectId = state.subjectIndex + 1;
              }
            } catch (e) {
              console.error('解析导航状态失败:', e);
            }
          }
          this.$router.push({
            path: `/mobile/class/${this.classId}`,
            query: query
          });
        } else {
          // 已经在主页，触发刷新
          this.$router.go(0);
        }
      } else if (nav === 'more') {
        if (this.activeNav !== 'more') {
          this.$router.push(`/mobile/more/${this.classId}`);
        } else {
          // 已经在更多页面，触发刷新
          this.$router.go(0);
        }
      }
    }
  }
}
</script>

<style scoped>
/* 导航栏基础样式 */
.top-nav-bar {
  display: flex;
  height: 44px;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  /* 平滑过渡动画，用于隐藏/显示效果 */
  transition: transform 0.3s ease;
}

/* 导航栏隐藏状态：向上平移100%实现滑出效果 */
.top-nav-bar.hidden {
  transform: translateY(-100%);
}

/* 管理页面主题色 */
.top-nav-bar.management-theme {
  background: linear-gradient(90deg, #40e0d0, #6abfd1);
}

/* 语文科目主题色 */
.top-nav-bar.chinese-subject {
  background: linear-gradient(90deg, #d2691e, #ff8c00);
}

/* 数学科目主题色 */
.top-nav-bar.math-subject {
  background: linear-gradient(90deg, #409EFF, #4169e1);
}

/* 英语科目主题色 */
.top-nav-bar.english-subject {
  background: linear-gradient(90deg, #ff69b4, #ff1493);
}

/* 导航项样式 */
.nav-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.nav-item i {
  font-size: 18px;
}

.nav-item:hover {
  color: rgba(255, 255, 255, 0.9);
}

/* 激活状态的导航项 */
.nav-item.active {
  color: #fff;
  font-weight: bold;
}

/* 激活状态下的底部指示条 */
.nav-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: #fff;
  border-radius: 3px 3px 0 0;
}
</style>
